import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin, Observable, of } from "rxjs";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { TaskOrgLevel } from "src/app/models/taskOrgLevel.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { TaskOrgLevelService } from "src/app/services/task-org-level.service";
import { TaskGroupService } from "src/app/services/taskGroup.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
    IOrganizationLevelEntity,
} from "src/app/shared/form/form.component";
import {
    HierarchicalSelectionFormField,
    SingleSelectionFormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";

export interface ITaskSiteDialogData
    extends IFormDialogData, IOrganizationLevelEntity {
    taskGroupId: string;
}

@Component({
  standalone: false,
    selector: "app-task-site-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class TaskSiteDialogComponent
    extends FormComponent<TaskOrgLevel>
    implements OnInit
{
    organizationLevels: OrganizationLevel[];
    taskGroups: ILookup[];

    constructor(
        dialogRef: MatDialogRef<TaskSiteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ITaskSiteDialogData,
        protected taskOrgLevelService: TaskOrgLevelService,
        protected taskGroupService: TaskGroupService,
        private organizationLevelService: OrganizationLevelService,
        protected injector: Injector,
    ) {
        super(
            dialogRef,
            taskOrgLevelService as any,
            data,
            "Task Group to Site",
            injector,
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(),
            taskGroups: this.taskGroupService.getAll(),
        }).subscribe({
            next: (result) => {
                this.entity = {
                    ...result.entity,
                    taskGroupId: this.data.taskGroupId,
                    organizationLevelId: this.data.organizationLevelId,
                };
                this.organizationLevels = result.organizationLevels;
                this.taskGroups = result.taskGroups;
                this.initFields();
                this.isFormLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
    }

    getEntity(): Observable<TaskOrgLevel> {
        return this.isAdd
            ? of(this.getNewObject())
            : (this.service as any).getByTaskGroupId(
                  this.data.taskGroupId,
                  this.data.organizationLevelId,
              );
    }

    getNewObject(): TaskOrgLevel {
        return new TaskOrgLevel();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<TaskOrgLevel>((x) => x.taskGroupId),
                options: this.taskGroups,
                name: "Task Group Name",
                columnSpan: new ColumnSpan(1, 1),
                readonly: true,
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<TaskOrgLevel>((x) => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: "Organization Level",
                columnSpan: new ColumnSpan(1, 1),
                required: true,
            }),
        ];
    }
}
